import { useEffect, useMemo, useState } from "react";
import format from 'date-fns/format';
import Select from '@mui/material/Select';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { DateRangePicker } from 'react-date-range';
import isNull from "../../utils/NullChecking";
import InputMask from 'react-input-mask';
import { addDays, isSameDay, startOfWeek, startOfMonth, subMonths, endOfMonth, endOfWeek, subDays } from 'date-fns';
import MonthDD from "../../utils/MonthDD";
import { useSelector } from "react-redux";

const CalendaRangeInput = (props) => {
    const { label, placeholder, searchParam, setSearchParam, options, defaultDates, DateType, SetDateType, maxDate, months, openCalendarPopup } = props;
    let DropdownData = useSelector(state => state.GeneralDropDownsReducer);
    const currentDate = DropdownData?.CurrentPracticeDate;

    // Parse the currentDate from Redux (assuming it's in 'MM/DD/YYYY' format like '03/31/2026')
    const getReferenceDate = () => {
        if (currentDate && currentDate !== '') {
            const [month, day, year] = currentDate.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        return new Date(); // fallback to system date if no Redux date
    };

    const referenceDate = getReferenceDate();
    // console.log(referenceDate, 'referenceDate from Redux');

    // Function to check if two dates are the same in a specific timezone
    const isSameDayInTimezone = (date1, date2) => {
        if (!date1 || !date2) return false;
        return date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear();
    };

    let minSlctdDate = new Date(new Date().getFullYear() - 100, new Date().getMonth(), new Date().getDate());
    let maxSlctdDate = new Date(new Date().getFullYear() + 20, new Date().getMonth(), new Date().getDate());
    const [dates, setDate] = useState();
    const [position, setPosition] = useState(null);
    const openRangePicker = Boolean(position);
    const [selectedOption, setSelectedOption] = useState('');
    const [value, setValue] = useState(null);
    const defaultDate = defaultDates ? defaultDates : '';

    // Custom date range definitions using referenceDate instead of new Date()
    const customRanges = [
        {
            label: 'Today',
            range: () => ({
                startDate: referenceDate,
                endDate: referenceDate,
            }),
            isSelected: (range) => isSameDay(range.startDate, referenceDate) && isSameDay(range.endDate, referenceDate),
        },
        {
            label: 'This Week',
            range: () => ({
                startDate: startOfWeek(referenceDate),
                endDate: referenceDate,
            }),
            isSelected: (range) =>
                isSameDay(range.startDate, startOfWeek(referenceDate)) && isSameDay(range.endDate, referenceDate),
        },
        {
            label: 'Last Week',
            range: () => ({
                startDate: startOfWeek(subDays(referenceDate, 7)),
                endDate: endOfWeek(subDays(referenceDate, 7)),
            }),
            isSelected: (range) =>
                isSameDay(range.startDate, startOfWeek(subDays(referenceDate, 7))) &&
                isSameDay(range.endDate, endOfWeek(subDays(referenceDate, 7))),
        },
        {
            label: 'This Month',
            range: () => ({
                startDate: startOfMonth(referenceDate),
                endDate: endOfMonth(referenceDate),
            }),
            isSelected: (range) =>
                isSameDay(range.startDate, startOfMonth(referenceDate)) &&
                isSameDay(range.endDate, endOfMonth(referenceDate)),
        },
        {
            label: 'Last 3 Months',
            range: () => ({
                startDate: subMonths(startOfMonth(referenceDate), 3),
                endDate: endOfMonth(referenceDate),
            }),
            isSelected: (range) =>
                isSameDay(range.startDate, subMonths(startOfMonth(referenceDate), 3)) &&
                isSameDay(range.endDate, endOfMonth(referenceDate)),
        },
    ];

    const [range, setRange] = useState([
        {
            startDate: referenceDate,
            endDate: null,
            key: 'selection'
        }
    ]);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const [dateType, setDateType] = useState(DateType || options[0].split(" ").join(""));

    /////////Date Type////////////////////
    const handleDateType = (event) => {
        setDateType(event.target.value);
        handleChange(dates, event.target.value)
    };

    //////////////////////////////////////
    useEffect(() => {
        let clear;
        for (let type of options) {
            if (isNull(searchParam[`${type.split(" ").join("")}From`])) { clear = true } else { clear = false; break; }
            if (isNull(searchParam[`${type.split(" ").join("")}To`])) { clear = true } else { clear = false; break; }
        }
        if (clear === true) {
            setValue("");
        }
    }, [searchParam, setSearchParam]);

    //////////////////////////////////////
    const handleChange = (dates, dateType) => {
        if (dates) {
            let startDate, endDate;
            if (typeof dates === 'string') {
                const regex = /^(\d{2}\/\d{2}\/\d{4})/;
                const regexDouble = /^(\d{2}\/\d{2}\/\d{4})-(\d{2}\/\d{2}\/\d{4})$/;
                const matches = dates.match(regex);
                const matchesDouble = dates.match(regexDouble);

                if (matchesDouble) {
                    startDate = matchesDouble[1].split("/");
                    endDate = matchesDouble[2].split("/");
                    dates = [{
                        startDate: new Date(startDate[2], startDate[0] - 1, startDate[1]),
                        endDate: new Date(endDate[2], endDate[0] - 1, endDate[1]),
                        key: "selection"
                    },];
                } else if (matches) {
                    startDate = matches[1].split("/");
                    dates = [{
                        startDate: new Date(startDate[2], startDate[0] - 1, startDate[1]),
                        endDate: null,
                        key: "selection"
                    },];
                } else {
                    console.log("Invalid dates string format");
                }

                if (dates[0].startDate < minSlctdDate) {
                    dates[0].startDate = minSlctdDate;
                }
                if (dates[0].startDate > maxSlctdDate) {
                    dates[0].startDate = maxSlctdDate;
                }
                if (dates[0].endDate < minSlctdDate) {
                    dates[0].endDate = minSlctdDate;
                }
                if (dates[0].endDate > maxSlctdDate) {
                    dates[0].endDate = maxSlctdDate;
                }
                setDate(dates);
                setRange(dates);
                setValue(format(dates[0].startDate, 'MM/dd/yyyy') + ' ‐ ' + format(dates[0].endDate, 'MM/dd/yyyy'));
            } else {
                setDate(dates);
                setRange(dates);
                setValue(format(dates[0].startDate, 'MM/dd/yyyy') + ' ‐ ' + format(dates[0].endDate, 'MM/dd/yyyy'));
            }

            for (let type of options) {
                if (type.split(" ").join("") !== dateType) {
                    searchParam[`${type.split(" ").join("")}From`] = null;
                    searchParam[`${type.split(" ").join("")}To`] = null;
                }
            }

            setSearchParam({
                ...searchParam,
                [`${dateType}From`]: format(dates[0].startDate, 'MM/dd/yyyy'),
                [`${dateType}To`]: format(dates[0].endDate, 'MM/dd/yyyy')
            });
        } else {
            setRange([
                {
                    startDate: null,
                    endDate: null,
                    key: 'selection'
                }
            ]);
            setSearchParam((prev) => ({
                ...prev,
                [`${dateType}From`]: null,
                [`${dateType}To`]: null,
            }));

            if(searchParam?.[`${dateType}From`] || searchParam?.[`${dateType}To`]){
                props?.setApplyDate?.(true);
            }
        }
    }

    //to be
    // useEffect(() => {
    //     if (openCalendarPopup) {
    //         setPosition(document.querySelector('.calender-icon'));
    //     } else {
    //         setPosition(null);
    //     }
    // }, [openCalendarPopup]);
    const show = (event) => { setPosition(event.currentTarget) };
    const hide = () => { setPosition(null) };
    const handleApply = () => { props?.setApplyDate?.(true); setPosition(null); };

    /////////////////////////////////////////
    // const handleCustomDate = (e) => {
    //     const val = e.target.value;
    //     setValue(val);
    //     if (val.replace(/_/g, "").length === 21) {
    //         handleChange(val, dateType);
    //     }
    // };
    const handleCustomDate = (e) => {
        const val = e.target.value;
        setValue(val);

        const cleanVal = val.replace(/_/g, "");

        // FULL DATE → normal flow
        if (cleanVal.length === 21) {
            handleChange(val, dateType);
        }
        // EMPTY ya incomplete → CLEAR model
        // else if (cleanVal.length < 10) {
        //     handleChange(null, dateType);
        // }
        else if (cleanVal.length === 0) {
            handleChange(null, dateType);
        }
    };

    const formatValue = (value) => {
        const formattedString = value.replace(/\D/g, '');
        let formattedValue = '';
        if (formattedString.length >= 1) { formattedValue += formattedString.slice(0, 2); }
        if (formattedString.length >= 3) { formattedValue += '/' + formattedString.slice(2, 4); }
        if (formattedString.length >= 5) { formattedValue += '/' + formattedString.slice(4, 8); }
        if (formattedString.length >= 9) { formattedValue += '-' + formattedString.slice(8, 10); }
        if (formattedString.length >= 11) { formattedValue += '/' + formattedString.slice(10, 12); }
        if (formattedString.length >= 13) { formattedValue += '/' + formattedString.slice(12, 16); }
        return formattedValue;
    };

    const handleSelectChange = (index, fetchData) => {
        setSelectedOption(index);
        const selectedShortcut = shortcutsItems[index];
        const range = selectedShortcut?.getValue();
        const startDate = range[0];
        const endDate = range[1];

        const newRange = [
            {
                startDate,
                endDate,
                key: 'selection'
            }
        ];

        setRange(newRange);
        setValue(format(startDate, 'MM/dd/yyyy') + ' ‐ ' + format(endDate, 'MM/dd/yyyy'));

        if (fetchData) {
            const updatedSearchParam = { ...searchParam };
            for (let type of options) {
                const cleanType = type.split(" ").join("");
                setDateType((prev) => {
                    if (cleanType !== prev) {
                        updatedSearchParam[`${cleanType}From`] = null;
                        updatedSearchParam[`${cleanType}To`] = null;
                    } else {
                        updatedSearchParam[`${prev}From`] = format(startDate, 'MM/dd/yyyy');
                        updatedSearchParam[`${prev}To`] = format(endDate, 'MM/dd/yyyy');
                    }
                    return prev;
                });
            }
            setSearchParam(updatedSearchParam);
        } else {
            setSearchParam({
                ...searchParam,
                [`${dateType}From`]: format(startDate, 'MM/dd/yyyy'),
                [`${dateType}To`]: format(endDate, 'MM/dd/yyyy')
            });
        }
    };

    ///////////////////////////////////////////////////
    useEffect(() => {
        if (!isNull(defaultDate)) {
            handleChange(defaultDate, dateType);
        }
    }, [defaultDate]);

    ///////////////////////////////////////////////////
    useEffect(() => {
        if (DateType) {
            let [dateTypes, month, year] = DateType?.split(" ");
            if (dateTypes) {
                setDateType(prev => dateTypes === "CreatedDate" ? "Check" : dateTypes === "SubmittedDate" ? "Submitted" : dateTypes);
            }
            if (!isNull(month)) {
                handleSelectChange(MonthDD.findIndex(it => it === month), true);
            }
            SetDateType('');
        }
    }, [DateType]);

    const [shownDate, setShownDate] = useState(referenceDate);

    const handleShownDateChange = (date) => {
        if (date.getFullYear() !== shownDate.getFullYear()) {
        }
        setShownDate(date);
    };

    const getCurrentYear = () => {
        return shownDate.getFullYear();
    };

    const shortcutsItems = useMemo(() =>
        monthNames.map((monthName, index) => ({
            label: monthName,
            getValue: () => {
                const year = getCurrentYear();
                const start = new Date(year, index, 1);
                const end = endOfMonth(start);
                return [start, end];
            },
        })), [shownDate]);

    return (<>
        <div className="input input calender hk-daterange-picker">
            {label === false ? null : <label htmlFor="">{label || "Date"}</label>}
            <div className='calender-icon'>
                <div className="calender-input w-100">
                    <InputMask className='form-control rounded'
                        {...(placeholder ? { placeholder: placeholder } : {})}
                        mask={"99/99/9999-99/99/9999"}
                        onChange={(e) => { handleCustomDate(e) }}
                        maskChar="_"
                        value={value} />
                    <span className="icon-calender" onClick={show}>
                        <span className="path1"></span>
                        <span className="path2"></span>
                        <span className="path3"></span>
                        <span className="path4"></span>
                        <span className="path5"></span>
                        <span className="path6"></span>
                    </span>
                </div>
                <span className="calender-btn btn-orange input-group-text p-0">
                    {options.length !== 1 &&
                        <Select
                            value={dateType}
                            onChange={handleDateType}
                            displayEmpty
                            className='app-mui-select-no-ui'>
                            {options.map((item) => {
                                return (
                                    <MenuItem value={item.split(" ").join("")}>{item.split(" ")[0]}</MenuItem>
                                )
                            })}
                        </Select>}
                </span>
            </div>
            <Menu
                className='calender-menu'
                anchorEl={position}
                open={openRangePicker}
                onClose={hide}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <div className='DateRangePicker-Wrapper'>
                    <DateRangePicker
                        onChange={item => handleChange([item.selection], dateType)}
                        editableDateInputs={true}
                        moveRangeOnFirstSelection={false}
                        ranges={range}
                        staticRanges={customRanges}
                        // months={months ? months : 2}
                        months={2}
                        direction="horizontal"
                        weekdayDisplayFormat={'EEEEE'}
                        maxDate={maxDate || undefined}
                        onShownDateChange={handleShownDateChange}
                        dayContentRenderer={(date) => {
                            // Check if this date is the current date from Redux
                            const isToday = isSameDayInTimezone(date, referenceDate);
                            return (
                                <div
                                    style={{
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <span
                                        style={{
                                            position: 'relative',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '28px',
                                            height: '28px',
                                            ...(isToday ? {
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid #ff0000'
                                            } : {})
                                        }}
                                    >
                                        {date.getDate()}
                                    </span>
                                </div>
                            );
                        }}
                    />
                    <div className='DateRangePicker-Action d-flex justify-content-between align-items-center px-1'>
                        <div className="input">
                            <select
                                className="form-control"
                                value={selectedOption}
                                onChange={(e) => handleSelectChange(e.target.value)}
                            >
                                <option value="">Select Month</option>
                                {shortcutsItems.map((item, index) => (
                                    <option key={index} value={index}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button className='btn btn-gray btn-sm' onClick={hide}>Cancel</button>
                            <button className='btn btn-orange btn-sm' onClick={handleApply}>Apply</button>
                        </div>
                    </div>
                </div>
            </Menu>
        </div>
    </>);
}

export default CalendaRangeInput;